import { $, $k } from "../lib/format";
import { ACCENT, ACCENT2, BG, BORDER, CARD, DARK, DIM, MID, RED } from "../theme";
import { Bar, BarChart, CartesianGrid, Cell, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ChartTip } from "../components/ChartTip";
import { EXCEL_LOAD_ERROR, loadXLSX } from "../lib/excel";
import { IncompleteChip } from "../components/IncompleteChip";
import { buildExcelRows } from "../excelRows";
import { buildPrintHTML, formatExportMoney, hasCents, printWhenReady } from "../printHtml";
import { clientInsight, clientRows, jobTypeInsight, jobTypeRows, worstJobRows, worstJobsInsight } from "../reportsOther";
import { excelNoteRows, monthlyInsight, monthlyReportNote, monthlyReportRows } from "../reportMonthly";
import { jobCompleteness, marginView } from "../jobCompleteness";
import { useBusinessType } from "../useBusinessType";
import { useState } from "react";

// ─── TAB: REPORTS ─────────────────────────────────────────────────────────────

// Excel export. SheetJS is bundled with the app and loaded the first time it is needed (see loadXLSX).
// Returns a promise that rejects with a plain-language Error when the Excel library cannot load.
function exportToExcel(data, title, note) {
  return loadXLSX().then(XLSX => doExcelExport(XLSX, data, title, note));
}

function doExcelExport(XLSX, data, title, note) {
  if (!data || data.length === 0) return;

  const keys    = Object.keys(data[0]);
  const headers = keys.map(k => k.charAt(0).toUpperCase() + k.slice(1).replace(/([A-Z])/g, ' $1').trim());
  const dateStr = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

  // ── Build rows (pure logic lives in excelRows.js) ──────────────────────────────────────────────────────────────
  // Title block: 4 rows before headers
  const TITLE_ROWS = 4;
  const HEADER_ROW = TITLE_ROWS;      // 0-indexed: row 4
  const DATA_START  = TITLE_ROWS + 1; // row 5

  const { types, dataRows, totalsRow } = buildExcelRows(data);

  // ── Create worksheet from AOA ───────────────────────────────────────────────
  const aoa = [
    ['Canopy Business Intelligence', ...Array(keys.length - 1).fill('')],
    [title, ...Array(keys.length - 1).fill('')],
    [`Generated: ${dateStr}`, ...Array(keys.length - 1).fill('')],
    Array(keys.length).fill(''),  // spacer
    headers,
    ...dataRows,
    totalsRow,
    ...excelNoteRows(note, keys.length),
  ];

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  const totalDataRows = dataRows.length;
  const totalsRowIdx  = DATA_START + totalDataRows; // 0-indexed

  // ── Column widths ───────────────────────────────────────────────────────────
  ws['!cols'] = types.map((t, i) => {
    if (i === 0)       return { wch: 30 };
    if (t === 'usd')   return { wch: 16 };
    if (t === 'pct')   return { wch: 12 };
    if (t === 'int')   return { wch: 10 };
    return { wch: 22 };
  });

  // ── Merge title cells across all columns ───────────────────────────────────
  ws['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: keys.length - 1 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: keys.length - 1 } },
    { s: { r: 2, c: 0 }, e: { r: 2, c: keys.length - 1 } },
    { s: { r: 3, c: 0 }, e: { r: 3, c: keys.length - 1 } },
  ];

  // ── Cell styles helper ─────────────────────────────────────────────────────
  function addr(r, c) {
    return XLSX.utils.encode_cell({ r, c });
  }

  function setStyle(cellAddr, style) {
    if (!ws[cellAddr]) ws[cellAddr] = { t: 'z', v: '' };
    ws[cellAddr].s = style;
  }

  // Colors
  const C_GREEN_DARK  = '1A3C2E';
  const C_GREEN_MID   = '2D6A4F';
  const C_GREEN_LIGHT = 'E8F0EC';
  const C_GRAY        = 'F2EFE9';
  const C_WHITE       = 'FFFFFF';
  const C_DARK        = '2C2416';
  const C_DIM         = 'A89880';

  // ── Title row styles ────────────────────────────────────────────────────────
  // Row 0 — "Canopy Business Intelligence"
  setStyle(addr(0, 0), {
    font: { bold: true, sz: 16, color: { rgb: C_GREEN_DARK }, name: 'Arial' },
    fill: { patternType: 'solid', fgColor: { rgb: C_WHITE } },
    alignment: { vertical: 'center' }
  });

  // Row 1 — report title
  setStyle(addr(1, 0), {
    font: { bold: true, sz: 13, color: { rgb: C_DARK }, name: 'Arial' },
    fill: { patternType: 'solid', fgColor: { rgb: C_WHITE } },
  });

  // Row 2 — date
  setStyle(addr(2, 0), {
    font: { italic: true, sz: 10, color: { rgb: C_DIM }, name: 'Arial' },
    fill: { patternType: 'solid', fgColor: { rgb: C_WHITE } },
  });

  // ── Header row styles (row 4) ───────────────────────────────────────────────
  keys.forEach((_, c) => {
    setStyle(addr(HEADER_ROW, c), {
      font: { bold: true, sz: 10, color: { rgb: C_WHITE }, name: 'Arial' },
      fill: { patternType: 'solid', fgColor: { rgb: C_GREEN_DARK } },
      alignment: { horizontal: c === 0 ? 'left' : 'right', vertical: 'center' },
      border: {
        bottom: { style: 'medium', color: { rgb: C_GREEN_MID } }
      }
    });
  });

  // ── Data row styles + number formats ───────────────────────────────────────
  dataRows.forEach((row, ri) => {
    const rowIdx = DATA_START + ri;
    const isAlt  = ri % 2 === 1;
    const bgColor = isAlt ? C_GRAY : C_WHITE;

    keys.forEach((k, c) => {
      const cellAddr = addr(rowIdx, c);
      if (!ws[cellAddr]) ws[cellAddr] = { t: 'z', v: '' };

      const baseStyle = {
        font: { sz: 10, color: { rgb: C_DARK }, name: 'Arial' },
        fill: { patternType: 'solid', fgColor: { rgb: bgColor } },
        alignment: { horizontal: c === 0 ? 'left' : 'right', vertical: 'center' },
        border: {
          bottom: { style: 'thin', color: { rgb: 'DDD5C4' } }
        }
      };

      // Only real numbers become number cells; an empty value (such as no margin) stays an empty cell.
      const isNum = typeof ws[cellAddr].v === 'number';
      if (types[c] === 'usd') {
        ws[cellAddr].z = '$#,##0';
        if (isNum) ws[cellAddr].t = 'n';
      } else if (types[c] === 'pct') {
        ws[cellAddr].z = '0.0%';
        if (isNum) ws[cellAddr].t = 'n';
      } else if (types[c] === 'int') {
        ws[cellAddr].z = '#,##0';
        if (isNum) ws[cellAddr].t = 'n';
      }

      ws[cellAddr].s = baseStyle;
    });
  });

  // ── Totals row styles ───────────────────────────────────────────────────────
  keys.forEach((k, c) => {
    const cellAddr = addr(totalsRowIdx, c);
    if (!ws[cellAddr]) ws[cellAddr] = { t: 'z', v: '' };

    ws[cellAddr].s = {
      font: { bold: true, sz: 10, color: { rgb: C_GREEN_DARK }, name: 'Arial' },
      fill: { patternType: 'solid', fgColor: { rgb: C_GREEN_LIGHT } },
      alignment: { horizontal: c === 0 ? 'left' : 'right', vertical: 'center' },
      border: {
        top: { style: 'medium', color: { rgb: C_GREEN_MID } }
      }
    };

    if (types[c] === 'usd') {
      ws[cellAddr].z = '$#,##0';
      ws[cellAddr].t = 'n';
    } else if (types[c] === 'pct') {
      // Totals don't sum percentages — clear the value
      ws[cellAddr].v = '';
      ws[cellAddr].t = 'z';
    } else if (types[c] === 'int') {
      ws[cellAddr].z = '#,##0';
      ws[cellAddr].t = 'n';
    }
  });

  // ── Row heights ─────────────────────────────────────────────────────────────
  ws['!rows'] = [];
  ws['!rows'][0] = { hpt: 28 }; // title
  ws['!rows'][1] = { hpt: 22 }; // report name
  ws['!rows'][2] = { hpt: 16 }; // date
  ws['!rows'][3] = { hpt: 8  }; // spacer
  ws['!rows'][HEADER_ROW] = { hpt: 20 }; // headers

  // ── Write file ──────────────────────────────────────────────────────────────
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, title.slice(0, 31));
  XLSX.writeFile(wb, `Canopy - ${title} - ${dateStr}.xlsx`);
}

// PDF export: builds the print page with buildPrintHTML (every value escaped) and opens it in a new window
function exportToPDF(title, insight, reportData, report) {
  const html = buildPrintHTML({
    title,
    description: report?.description,
    insight,
    data: reportData,
    note: report?.note?.(),
  });

  const printWindow = window.open('', '_blank', 'width=900,height=700');
  if (!printWindow) {
    alert('Please allow popups for app.canopybi.com to use PDF export.');
    return;
  }
  // The new window shares this app's origin; cut its link back to the app.
  printWindow.opener = null;
  printWindow.document.write(html);
  printWindow.document.close();
  printWhenReady(printWindow);
}

function ExportButtons({ data, title, insight, report, compact = false }) {
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");

  function handleExcel() {
    setExporting(true);
    setExportError("");
    setTimeout(async () => {
      try {
        await exportToExcel(data, title, report?.note?.());
      } catch (err) {
        console.error('Excel export failed:', err);
        setExportError(err?.message === EXCEL_LOAD_ERROR ? EXCEL_LOAD_ERROR : "The Excel file could not be made. Try again.");
      }
      setExporting(false);
    }, 100);
  }

  function handlePDF() {
    exportToPDF(title, insight, data, report);
  }

  if (compact) {
    return (
      <div className="r-wrap" style={{ display:"flex",gap:6 }}>
        <button className="btn" onClick={handlePDF} title="Export to PDF" style={{ fontSize:10,padding:"4px 10px" }}>PDF</button>
        <button className="btn" onClick={handleExcel} disabled={exporting} title="Export to Excel" style={{ fontSize:10,padding:"4px 10px" }}>{exporting?"…":"XLS"}</button>
        {exportError && <span role="alert" style={{ fontSize:11,color:RED,fontFamily:"'DM Sans',sans-serif" }}>{exportError}</span>}
      </div>
    );
  }

  return (
    <div className="r-wrap" style={{ display:"flex",gap:8 }}>
      <button className="btn no-print" onClick={handlePDF} style={{ fontSize:11,display:"flex",alignItems:"center",gap:5 }}>
        <span style={{ fontSize:13 }}>⬇</span> Export PDF
      </button>
      <button className="btn no-print" onClick={handleExcel} disabled={exporting} style={{ fontSize:11,display:"flex",alignItems:"center",gap:5 }}>
        <span style={{ fontSize:13 }}>⬇</span> {exporting ? "Preparing…" : "Export Excel"}
      </button>
      {exportError && <span role="alert" style={{ fontSize:12,color:RED,fontFamily:"'DM Sans',sans-serif",alignSelf:"center" }}>{exportError}</span>}
    </div>
  );
}

function Reports({ jobSummaries, pendingItems }) {
  const { rule } = useBusinessType();
  const [activeReport, setActiveReport] = useState(null);

  const REPORTS = [
    {
      id: "top-job-types",
      title: "Most Profitable Job Type",
      description: "Average margin by job category — see which type of work earns you the most.",
      icon: "▲",
      compute: () => jobTypeRows(jobSummaries),
      chartType: "bar", dataKey: "margin", color: ACCENT2, yLabel: "% Margin",
      insight: (data) => jobTypeInsight(data, formatExportMoney),
    },
    {
      id: "worst-jobs",
      title: "Worst Performing Jobs",
      description: "Bottom 5 jobs by profit — understand where money is being lost.",
      icon: "▼",
      compute: () => worstJobRows(jobSummaries),
      chartType: "bar", dataKey: "profit", color: RED, yLabel: "$ Profit",
      insight: (data) => worstJobsInsight(data, formatExportMoney),
    },
    {
      id: "monthly-trend",
      title: "Monthly Profit Trend",
      description: "Revenue, costs, and profit over time — see how your business is tracking.",
      icon: "📈",
      // Only the owner's own dated months. With none, the report is empty and says so (never sample months).
      compute: () => monthlyReportRows(jobSummaries),
      emptyText: "No dated activity yet.",
      note: () => monthlyReportNote(jobSummaries),
      chartType: "line", dataKey: "profit", color: ACCENT2, yLabel: "$ Amount",
      insight: (data) => monthlyInsight(data, $),
    },
    {
      id: "client-ranking",
      title: "Client Profitability Ranking",
      description: "Total profit generated per client — know your most valuable relationships.",
      icon: "★",
      compute: () => clientRows(jobSummaries),
      chartType: "bar", dataKey: "profit", color: ACCENT, yLabel: "$ Profit",
      insight: (data) => clientInsight(data, formatExportMoney),
    },
  ];

  const report     = activeReport ? REPORTS.find(r => r.id === activeReport) : null;
  const reportData = report ? report.compute() : [];

  // ── Individual report view ──
  if (report) {
    const insight = report.insight(reportData);
    const reportNote = report.note ? report.note() : "";
    // Money in the data table keeps its minus sign; a column shows cents on every row if any row has cents (same rule as the PDF).
    const tableCents = {};
    Object.keys(reportData[0]||{}).forEach(k => { tableCents[k] = k !== "margin" && k !== "jobs" && reportData.some(r => hasCents(r[k])); });
    return (
      <div className="r-page" style={{ padding:"32px 36px", background:BG, minHeight:"100vh" }}>

        {/* Header row */}
        <div className="r-head" style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:28,gap:12 }}>
          <div className="r-stack" style={{ display:"flex",alignItems:"center",gap:14 }}>
            <button className="btn no-print" onClick={()=>setActiveReport(null)}>← All Reports</button>
            <div>
              <h1 style={{ fontFamily:"'Lora',serif",fontSize:24,fontWeight:600,color:DARK,letterSpacing:"-0.02em" }}>{report.title}</h1>
              <p style={{ fontFamily:"'DM Sans',sans-serif",fontSize:13,color:DIM,marginTop:4 }}>{report.description}</p>
            </div>
          </div>
          {/* Export buttons — full size on individual report page */}
          {reportData.length > 0 && <ExportButtons data={reportData} title={report.title} insight={insight} report={report} />}
        </div>

        {/* Print-only header (hidden on screen, shown when printing) */}
        <div className="print-only" style={{ display:"none",marginBottom:20 }}>
          <div style={{ fontFamily:"Arial,sans-serif",fontSize:10,color:"#888",marginBottom:4 }}>Canopy Business Intelligence · {new Date().toLocaleDateString('en-US',{month:'long',day:'numeric',year:'numeric'})}</div>
          <div style={{ fontFamily:"Arial,sans-serif",fontSize:18,fontWeight:"bold",color:"#2C2416" }}>{report.title}</div>
          <div style={{ fontFamily:"Arial,sans-serif",fontSize:12,color:"#6B5E4E",marginTop:4 }}>{report.description}</div>
        </div>

        {/* Canopy Insight card (skipped when the report is empty, so there is one message only) */}
        {reportData.length > 0 && (
        <div style={{ marginBottom:24,padding:"16px 20px",borderRadius:6,border:`1px solid rgba(92,122,90,0.25)`,background:"rgba(92,122,90,0.04)" }}>
          <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:9,letterSpacing:"0.12em",color:ACCENT2,textTransform:"uppercase",marginBottom:8,fontWeight:500 }}>Canopy Insight</div>
          <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:13,color:MID,lineHeight:1.7 }}>{insight}</div>
        </div>
        )}

        {reportData.length === 0 && (
          <div className="card" style={{ padding:"32px 26px",marginBottom:24,textAlign:"center" }}>
            <div style={{ fontFamily:"'Lora',serif",fontSize:15,color:MID,fontStyle:"italic",marginBottom:8 }}>{report.emptyText || "Nothing to show yet."}</div>
            <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:12,color:DIM }}>Dated invoices and expenses will show here.</div>
          </div>
        )}

        {/* Chart */}
        {reportData.length > 0 && (
        <div className="card r-card-pad" style={{ padding:"22px 26px",marginBottom:24 }}>
          <ResponsiveContainer width="100%" height={320}>
            {report.chartType === "bar" ? (
              <BarChart data={reportData} margin={{ top:4,right:4,left:16,bottom:60 }}>
                <CartesianGrid strokeDasharray="2 4" stroke={BORDER} vertical={false}/>
                <XAxis dataKey="name" interval={0} height={80} tick={({ x, y, payload }) => (
                  <g transform={`translate(${x},${y})`}>
                    <text x={0} y={0} dy={4} textAnchor="end" fill={DIM} fontSize={9} fontFamily="DM Mono" transform="rotate(-45)">
                      {payload.value.length > 16 ? payload.value.slice(0,16)+"…" : payload.value}
                    </text>
                  </g>
                )}/>
                <YAxis tick={{ fontSize:10,fill:DIM,fontFamily:"DM Mono" }} tickFormatter={$k} axisLine={false} tickLine={false} width={56}/>
                <Tooltip formatter={(v) => [v == null ? "—" : report.dataKey==="margin"?`${v}%`:$(v), report.yLabel]} contentStyle={{ background:CARD,border:`1px solid ${BORDER}`,borderRadius:5,fontFamily:"'DM Mono',monospace",fontSize:11 }}/>
                <ReferenceLine y={0} stroke={BORDER}/>
                <Bar dataKey={report.dataKey} radius={[3,3,0,0]}>
                  {reportData.map((e,i) => <Cell key={i} fill={(e[report.dataKey]||0)>=0?report.color:RED} opacity={0.85}/>)}
                </Bar>
              </BarChart>
            ) : (
              <LineChart data={reportData} margin={{ top:4,right:16,left:16,bottom:0 }}>
                <CartesianGrid strokeDasharray="2 4" stroke={BORDER} vertical={false}/>
                <XAxis dataKey="month" tick={{ fontSize:10,fill:DIM,fontFamily:"DM Mono" }} axisLine={false} tickLine={false}/>
                <YAxis tick={{ fontSize:10,fill:DIM,fontFamily:"DM Mono" }} tickFormatter={$k} axisLine={false} tickLine={false} width={56}/>
                <Tooltip content={ChartTip}/>
                <Line type="monotone" dataKey="revenue" stroke={DIM} strokeWidth={1.5} dot={false} name="Revenue"/>
                <Line type="monotone" dataKey="costs" stroke={RED} strokeWidth={1.5} dot={false} name="Costs" strokeDasharray="4 2"/>
                <Line type="monotone" dataKey="profit" stroke={ACCENT2} strokeWidth={2.5} dot={{ r:3,fill:ACCENT2 }} name="Profit"/>
              </LineChart>
            )}
          </ResponsiveContainer>
        </div>
        )}

        {reportNote && (
          <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:12,color:DIM,marginBottom:16 }}>{reportNote}</div>
        )}

        {/* Data table */}
        {reportData.length > 0 && (
        <div className="card" style={{ overflow:"hidden" }}>
          <div style={{ padding:"14px 20px",borderBottom:`1px solid ${BORDER}`,background:BG,display:"flex",justifyContent:"space-between",alignItems:"center" }}>
            <div style={{ fontFamily:"'Lora',serif",fontSize:13,color:MID,fontStyle:"italic" }}>Underlying data</div>
          </div>
          <div className="r-scroll">
          <table className="raw-table" style={{ width:"100%" }}>
            <thead>
              <tr>{Object.keys(reportData[0]||{}).filter(k=>!(report.id==="monthly-trend"&&k==="date")).map(k=>(
                <th key={k} style={{ textTransform:"capitalize" }}>
                  {k.replace(/([A-Z])/g,' $1').replace(/^./,s=>s.toUpperCase())}
                </th>
              ))}</tr>
            </thead>
            <tbody>
              {reportData.map((row,i) => (
                <tr key={i}>
                  {Object.entries(row).filter(([k])=>!(report.id==="monthly-trend"&&k==="date")).map(([k,v],j) => (
                    <td key={j} className={typeof v==="number"&&k!=="jobs"?"mono":""}>
                      {typeof v==="number"&&k!=="jobs" ? (k==="margin"?`${v}%`:formatExportMoney(v, tableCents[k])) : v==null ? "—" : v}
                      {report.id==="worst-jobs" && k==="name" && (() => { const jb = jobSummaries.find(x => x.name === v); if (!jb) return null; const jc = jobCompleteness(jb, pendingItems || [], rule); return <> <IncompleteChip completeness={jc} margin={marginView(jb, jc)}/></>; })()}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
        )}
      </div>
    );
  }

  // ── Report library view ──
  return (
    <div className="r-page" style={{ padding:"32px 36px", background:BG, minHeight:"100vh" }}>
      <div style={{ marginBottom:28 }}>
        <h1 style={{ fontFamily:"'Lora',serif",fontSize:24,fontWeight:600,color:DARK,letterSpacing:"-0.02em" }}>Report Library</h1>
        <p style={{ fontFamily:"'DM Sans',sans-serif",fontSize:13,color:DIM,marginTop:4 }}>Pre-built reports — open to view, export as PDF or Excel.</p>
      </div>
      <div style={{ display:"flex",flexDirection:"column",gap:12,maxWidth:720 }}>
        {REPORTS.map(r => {
          const data = r.compute();
          const topItem = data[0];
          return (
            <div key={r.id} className="card r-stack r-card-pad" style={{ padding:"18px 24px",transition:"border-color 0.15s",cursor:"pointer",display:"flex",alignItems:"center",gap:20 }}
              onClick={()=>setActiveReport(r.id)}
              onMouseOver={e=>e.currentTarget.style.borderColor=ACCENT}
              onMouseOut={e=>e.currentTarget.style.borderColor=BORDER}
            >
              <div style={{ flex:1 }}>
                <div style={{ fontFamily:"'Lora',serif",fontSize:15,fontWeight:500,color:DARK,marginBottom:4 }}>{r.title}</div>
                <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:11,color:DIM,lineHeight:1.5 }}>{r.description}</div>
                {!topItem && (
                  <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:11,color:DIM,marginTop:5,fontStyle:"italic" }}>
                    {r.emptyText || "Nothing to show yet."} Dated invoices and expenses will show here.
                  </div>
                )}
                {topItem && (
                  <div style={{ fontFamily:"'DM Sans',sans-serif",fontSize:11,color:DIM,marginTop:5 }}>
                    Top result: <span style={{ color:DARK,fontWeight:500 }}>
                      {topItem.name}{topItem.margin != null ? ` · ${topItem.margin}% margin` : topItem.profit != null ? ` · ${$(topItem.profit)}` : ""}
                    </span>
                  </div>
                )}
              </div>
              <div style={{ display:"flex",gap:6,flexShrink:0 }} onClick={e => e.stopPropagation()}>
                {data.length > 0 && <ExportButtons data={data} title={r.title} insight={r.insight(data)} report={r} compact={true} />}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export { Reports };
